import React, { useState, useEffect, useMemo } from "react";
import "./InputSideBar.css";
import "./OutputSideBar.css";
import MyLocationMap from './components/Map'
import Diagram from './components/Diagram'
import DiagramPayback from './components/DiagramPayback'

import {
    computeCost, computeCostMax, computeCostMin, computeSavings, computeSavingsMax, computeSavingsMin
} from './utils/costCalculator';


export default function InputSideBar() {
    const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight);

    useEffect(() => {
        const handleResize = () => {
            setIsLandscape(window.innerWidth > window.innerHeight);
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, []);

    const [openSection, setOpenSection] = useState("address");
    const [selectedPolicy, setSelectedPolicy] = useState(null);
    const [dragging, setDragging] = useState(false);

    const [priceBuy, setPriceBuy] = useState(0.35);
    const [priceSell, setPriceSell] = useState(0.07);

    const [fadeOut, setFadeOut] = useState(false);
    const [displayedPolicy, setDisplayedPolicy] = useState(selectedPolicy);

    useEffect(() => {
        if (selectedPolicy !== displayedPolicy) {
            setFadeOut(true);
            const timeout = setTimeout(() => {
                setDisplayedPolicy(selectedPolicy);
                setFadeOut(false);
            }, 300);

            return () => clearTimeout(timeout);
        }
    }, [selectedPolicy, displayedPolicy]);

    const [results, setResults] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [sizingError, setSizingError] = useState("");

    const [householdLoad, setHouseholdLoad] = useState(0);
    const [evLoad, setEVLoad] = useState(0);
    const [totalLoad, setTotalLoad] = useState(0);
    const [pvProduction, setPVProduction] = useState(0);
    const [battery, setBattery] = useState(-1);
    const [pv, setPV] = useState(-1);
    const [cost, setCost] = useState(-1);
    const [showValidateWindow, setShowValidateWindow] = useState(false);

    const openValidateWindow = () => {
        if (!isFading) {
            setShowValidateWindow(true);
        }
    };

    const closeValidateWindow = () => {
        setShowValidateWindow(false);
        setBattery(-1);
        setPV(-1);
        setCost(-1);
        setSizingError("")
    };

    const [formData, setFormData] = useState({
        address: "",
        lat: null,
        lon: null,
        electricity_kwh_per_year: null,
        self_sufficiency_target: "",
        charging_policy: "",

        has_ev: false,
        commute_distance_km: null,
        non_commute_distance_km: null,
        non_commute_trips_per_week: null,
        commute_days: [],
        ev_battery_capacity_kwh: null,

        heatpump: false,
        inductionCooking: false,
        oven: 2000,
        microwave: 800,
        stoveVentilation: 100,
        inductionStove: 0,
        bigFridgeMin: 80,
        bigFridgeMax: 120,
        smallFridgeMin: 0,
        smallFridgeMax: 0,
        kettle: 2000,
        iron: 2000,
        vacuum: 1500,
        houseVentilation: 50,
        household: "SingleWorker"
    });

    const usageFields = [
        // { name: "heatpump", label: "Heatpump", type: "checkbox" },
        { name: "oven", label: "Oven (W)", type: "number" },
        { name: "microwave", label: "Microwave (W)", type: "number" },
        { name: "stoveVentilation", label: "Stove Ventilation (W)", type: "number" },
        {
            name: "bigFridge",
            label: "Fridge (W): ",
            type: "group",
            fields: [
                { name: "bigFridgeMin", label: "Min", type: "number" },
                { name: "bigFridgeMax", label: "Max", type: "number" },
            ],
        },
        { name: "kettle", label: "Kettle (W)", type: "number" },
        { name: "iron", label: "Iron (W)", type: "number" },
        { name: "vacuum", label: "Vacuum Cleaner (W)", type: "number" },
        { name: "houseVentilation", label: "House Ventilation (W)", type: "number" },
    ];

    const householdOptions = [
        { value: "SingleWorker", label: "One adult working full-time" },
        { value: "DualWorker", label: "Two adults working full-time" },
        { value: "DualWorkerParttime", label: "One adult working full-time + one adult working part-time" },
        { value: "FamilyDualWorker", label: "Family with two adults working full-time" },
        { value: "FamilyDualWorkerParttime", label: "Family with one adult working full-time + one adult working part-time" },
        { value: "SingleRetired", label: "One pensioner" },
        { value: "DualRetired", label: "Two pensioners" },
    ];

    const [skipConsumption, setSkipConsumption] = useState(true);
    const [skipInductionStove, setSkipInductionStove] = useState(true);
    const [skipSmallFridge, setSkipSmallFridge] = useState(true);

    const toggleSection = (section) => {
        setOpenSection((prev) => (prev === section ? null : section));
    };

    function sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    const handleChange = async (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
        if (results !== null) {
            setIsFading(true)
            await sleep(2000);
            setResults(null);
        }
    };

    const handleChangeResults = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const [selectedIndex, setSelectedIndex] = useState(0);
    const [selectedGraph, setSelectedGraph] = useState("cost");

    const uniquePolicies = useMemo(() => {
        return results
            ? [...new Set(Object.values(results).map(([, , , , policy]) => policy))]
            : [];
    }, [results]);

    useEffect(() => {
        if (results && uniquePolicies.length > 0 && !selectedPolicy) {
            const defaultPolicy = uniquePolicies[0];
            setSelectedPolicy(defaultPolicy);

            const entry = Object.values(results).find(([, , , , policy]) => policy === defaultPolicy);
            if (entry) {
                const eue_index = entry[7];
                setSelectedIndex(eue_index);
            }
            console.log('Default policy:', defaultPolicy);
            console.log('Unique policies:', uniquePolicies);
        }
    }, [results, uniquePolicies, selectedPolicy]);


    useEffect(() => {
        handleChangeResults("self_sufficiency_target", selectedIndex + 20)
    }, [selectedIndex]);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFading, setIsFading] = useState(true);

    const [isSubmittingValidation, setIsSubmittingValidation] = useState(false);

    useEffect(() => {
        // Trigger fade-in after component mounts
        setIsFading(false);
    }, []);

    const handleCheckboxChange = (day) => {
        setFormData((prev) => {
            const updatedDays = prev.commute_days.includes(day)
                ? prev.commute_days.filter((d) => d !== day)
                : [...prev.commute_days, day];
            return { ...prev, commute_days: updatedDays };
        });
    };

    const validateFormData = () => {
        // Example checks – adjust to your needs!
        if (!formData.lat || !formData.lon) {
            return "Address is required.";
        }

        if ((formData.electricity_kwh_per_year !== null && !isNaN(formData.electricity_kwh_per_year)) && (formData.electricity_kwh_per_year < 1000 || formData.electricity_kwh_per_year > 15000)) {
            return "Yearly electricity consumption should be between 1,000 and 15,000 kWh.";
        }

        if (formData.has_ev) {
            if (formData.commute_distance_km === null || isNaN(formData.commute_distance_km) || formData.non_commute_distance_km < 0 || formData.non_commute_distance_km > 60) {
                return "Commute distance must be between 0 km and 60 km.";
            }
            if (formData.non_commute_trips_per_week === null || isNaN(formData.non_commute_trips_per_week) || formData.non_commute_trips_per_week < 0 || formData.non_commute_trips_per_week > 7) {
                return "Number of non-commute trips must be between 0 and 7.";
            }
            if (formData.non_commute_distance_km === null || isNaN(formData.non_commute_distance_km) || formData.non_commute_distance_km < 0 || formData.non_commute_distance_km > 20) {
                return "Regular short trips must be between 0 km and 20 km.";
            }
            if (formData.ev_battery_capacity_kwh === null || isNaN(formData.ev_battery_capacity_kwh) || formData.ev_battery_capacity_kwh < 21 || formData.ev_battery_capacity_kwh > 118) {
                return "EV battery capacity must be between 21 kWh and 118 kWh.";
            }
        }
        // console.log(formData)

        // Check appliances
        const applianceFields = [
            "oven",
            "microwave",
            "stoveVentilation",
            "bigFridgeMin",
            "bigFridgeMax",
            "kettle",
            "iron",
            "vacuum",
            "houseVentilation",
        ];

        for (const field of applianceFields) {
            const value = formData[field];
            if (value === null || value === undefined || value === "") {
                return `Please enter a value for ${field}.`;
            }
            if (isNaN(value)) {
                return `${field} must be a number.`;
            }
        }

        return null; // No errors found
    };


    const handleSubmit = async () => {
        if (!isSubmitting) {
            setErrorMessage("")
            const validationError = validateFormData();
            // console.log(validationError)
            if (validationError) {
                setErrorMessage(validationError);
                return; // Stop submitting
            }

            try {
                setIsSubmitting(true);
                setIsFading(true)
                setSelectedPolicy(null)
                setResults(null)

                const response = await fetch("/api/get-sizing", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(formData),
                });

                if (!response.ok) {
                    // Try to read the JSON error message
                    let errorMessage = "Failed to submit form.";
                    try {
                        const errorData = await response.json();
                        if (errorData.message) {
                            errorMessage = errorData.message;
                        }
                    } catch (jsonError) {
                        console.error("Could not parse error JSON:", jsonError);
                    }

                    throw new Error(errorMessage);
                }

                const sizing = await response.json();
                console.log("Submission successful:", sizing);
                setResults(sizing.prediction);
                setEVLoad(sizing.ev_load);
                setHouseholdLoad(sizing.household_load);
                setTotalLoad(sizing.total_load);
                setPVProduction(sizing.production_1pv);

                const defaultPolicy = [...new Set(Object.values(sizing.prediction).map(([, , , , policy]) => policy))][0];
                setSelectedPolicy(defaultPolicy);
                handleChangeResults("charging_policy", defaultPolicy)

                const entry = Object.values(sizing.prediction).find(([, , , , policy]) => policy === defaultPolicy);
                if (entry) {
                    const eue_index = entry[7];
                    setSelectedIndex(eue_index);
                }
                setIsFading(false);
                setIsSubmitting(false);

            } catch (error) {
                console.error("Error submitting form:", error);
                alert(error.message);
            } finally {
                setIsFading(false);
                setIsSubmitting(false);
            }
        }
    };

    const handleSubmitValidate = async () => {
        setErrorMessage("")
        const validationError = validateFormData();
        if (validationError) {
            setErrorMessage(validationError);
            closeValidateWindow()
            return; // Stop submitting
        }

        try {
            setIsSubmittingValidation(true);

            setBattery(-1);
            setPV(-1);
            setCost(-1);

            const response = await fetch("/api/get-sizing-validation", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(formData),
            });

            if (!response.ok) {
                setIsSubmittingValidation(false);
                throw new Error("Failed to submit form");

            }

            const sizing = await response.json();
            console.log("Submission successful:", sizing);

            setBattery(sizing.battery);
            setPV(sizing.pv);
            setCost(sizing.cost);
            setEVLoad(sizing.ev_load);
            setHouseholdLoad(sizing.household_load);
            setTotalLoad(sizing.total_load);
            setPVProduction(sizing.production_1pv);
            setSizingError(sizing.error)

            setIsSubmittingValidation(false);

        } catch (error) {
            console.error("Error submitting form:", error);
            alert("There was an error submitting the form. Check console.");
        }
    };



    const [isLoadingGeocode, setIsLoadingGeocode] = useState(false);
    const [geocodeError, setGeocodeError] = useState(null);

    const handleGeocodeAddress = async () => {
        if (!formData.address.trim()) {
            setGeocodeError("Please enter an address.");
            return;
        }

        setIsLoadingGeocode(true);
        setGeocodeError(null);
        handleChange("lat", null);
        handleChange("lon", null);

        try {
            const response = await fetch(`/api/geocode-address`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ address: formData.address }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to geocode address');
            }

            const data = await response.json();
            if (data.address) {
                handleChange("address", data.address)
            }
            if (data.latitude && data.longitude) {
                handleChange("lat", data.latitude);
                handleChange("lon", data.longitude);
            } else {
                setGeocodeError("Could not find coordinates for the provided address.");
            }
        } catch (error) {
            console.error("Geocoding API error:", error);
            setGeocodeError(error.message || "An error occurred while geocoding the address.");
        } finally {
            setIsLoadingGeocode(false);
        }
    };

    return (
        <>
            {isLandscape ? (
                <div className="mainContainer">
                    <div className="inputSideBar">
                        <h2>SolExplore: Solar & Battery Planner</h2>

                        <div className="section">
                            <div className="sectionTitle" onClick={() => toggleSection("address")}>
                                🏠 Address 🏠
                            </div>
                            {openSection === "address" && (
                                <div className="sectionContent">
                                    <label>
                                        <input
                                            type="text"
                                            className="input"
                                            placeholder="Please enter your address"
                                            value={formData.address}
                                            onChange={(e) => handleChange("address", e.target.value)}
                                        />
                                        <div className="centerItems">
                                            <button
                                                onClick={handleGeocodeAddress}
                                                disabled={isLoadingGeocode || !formData.address.trim()}
                                                className="submitButton"
                                                style={{ marginTop: '10px', padding: '8px 15px', width: '30%' }}
                                            >
                                                {isLoadingGeocode ? 'Searching...' : 'Search Address'}
                                            </button>
                                        </div>
                                    </label>

                                    {geocodeError && (
                                        <p style={{ color: 'red', marginTop: '10px' }}>{geocodeError}</p>
                                    )}

                                    {formData.lat !== null && formData.lon !== null && ( // Only render map if coordinates are available
                                        <MyLocationMap latitude={formData.lat} longitude={formData.lon} />
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="section">
                            <div className="sectionTitle" onClick={() => toggleSection("usage")}>
                                ⚡️ Electricity Usage ⚡️
                            </div>
                            {openSection === "usage" && (
                                <div className="sectionContent">
                                    <div className="groupRow">

                                        <span>Household Type:</span>
                                        <div key="household" className="groupItem">

                                            <select
                                                className="input"
                                                value={formData.household}
                                                style={{ width: "100%" }}
                                                onChange={(e) => handleChange("household", e.target.value)}
                                            >
                                                {householdOptions.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>

                                        </div>
                                    </div>
                                    {usageFields.map((field) => {

                                        if (field.type === "group") {
                                            return (
                                                <div key={field.name} className="groupRow">
                                                    <span>{field.label}</span>
                                                    {field.fields.map((subField) => (
                                                        <div key={subField.name} className="groupItem">
                                                            <span>{subField.label}:</span>
                                                            <input
                                                                type={subField.type}
                                                                className="input"
                                                                style={{ width: "50px" }}
                                                                value={formData[subField.name]}
                                                                onChange={(e) =>
                                                                    handleChange(subField.name, parseInt(e.target.value))
                                                                }
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        }

                                        return (
                                            <div className="groupRow">
                                                <span>{field.label}:</span>

                                                <div key={field.name} className="groupItem">
                                                    <input
                                                        type={field.type}
                                                        className="input"
                                                        style={{ width: "50px" }}
                                                        value={formData[field.name]}
                                                        onChange={(e) =>
                                                            handleChange(field.name, parseInt(e.target.value))
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}

                                    <div>
                                        {skipInductionStove ? (
                                            <button
                                                className="submitButton"
                                                onClick={() => {
                                                    setSkipInductionStove(false);
                                                    handleChange("inductionStove", 2200);
                                                }}

                                            >
                                                ➕ Add Induction Stove
                                            </button>
                                        ) : (
                                            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                                <div className="groupRow">
                                                    <span>Induction Stove (W):</span>
                                                    <input
                                                        type="number"
                                                        className="input"
                                                        value={formData.inductionStove}
                                                        onChange={(e) =>
                                                            handleChange(
                                                                "inductionStove",
                                                                parseInt(e.target.value)
                                                            )
                                                        }
                                                    />
                                                    <button
                                                        className="submitButton"
                                                        type="button"
                                                        onClick={() => {
                                                            handleChange("inductionStove", "");
                                                            setSkipInductionStove(true);
                                                        }}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </label>
                                        )}
                                    </div>
                                    <div>
                                        {skipSmallFridge ? (
                                            <button
                                                className="submitButton"
                                                onClick={() => {
                                                    setSkipSmallFridge(false);
                                                    handleChange("smallFridgeMin", 50);
                                                    handleChange("smallFridgeMax", 80);
                                                }}
                                            >
                                                ➕ Add Small Fridge
                                            </button>
                                        ) : (
                                            <div key="smallFridge" className="groupRow">
                                                <span>Small Fridge (W):</span>
                                                {[
                                                    { name: "smallFridgeMin", label: "Min", type: "number" },
                                                    { name: "smallFridgeMax", label: "Max", type: "number" },
                                                ].map((subField) => (
                                                    <div key={subField.name} className="groupItem">
                                                        <span>{subField.label}:</span>
                                                        <input
                                                            type={subField.type}
                                                            className="input"
                                                            style={{ width: "50px" }}
                                                            value={formData[subField.name]}
                                                            onChange={(e) =>
                                                                handleChange(subField.name, parseInt(e.target.value))
                                                            }
                                                        />
                                                    </div>
                                                ))}
                                                <button
                                                    className="submitButton"
                                                    type="button"
                                                    onClick={() => {
                                                        handleChange("smallFridgeMin", "");
                                                        handleChange("smallFridgeMax", "");
                                                        setSkipSmallFridge(true);
                                                    }}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        {skipConsumption ? (
                                            <button className="submitButton" onClick={() => setSkipConsumption(false)}>
                                                ➕ Define Yearly Consumption
                                            </button>
                                        ) : (
                                            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                                <div className="groupRow">
                                                    <span>Total Yearly Consumption excluding EV (kWh):</span>
                                                    <input
                                                        type="number"
                                                        className="input"
                                                        value={formData.electricity_kwh_per_year}
                                                        onChange={(e) =>
                                                            handleChange(
                                                                "electricity_kwh_per_year",
                                                                parseInt(e.target.value)
                                                            )
                                                        }
                                                    />
                                                    <button
                                                        className="submitButton"
                                                        type="button"
                                                        onClick={() => {
                                                            handleChange("electricity_kwh_per_year", null);
                                                            setSkipConsumption(true);
                                                        }}
                                                    >
                                                        Skip
                                                    </button>
                                                </div>

                                            </label>
                                        )}
                                    </div>


                                </div>
                            )}

                        </div>

                        <div className="section">
                            <div className="sectionTitle" onClick={() => toggleSection("car")}>
                                🚗 Electric Car 🚗
                            </div>
                            {openSection === "car" && (
                                <div className="sectionContent">
                                    <label>
                                        Do you own an eletric vehicle?
                                        <select
                                            className="input"
                                            value={formData.has_ev ? "yes" : "no"}
                                            onChange={(e) => handleChange("has_ev", e.target.value === "yes")}
                                        >
                                            <option value="no">No</option>
                                            <option value="yes">Yes</option>
                                        </select>
                                    </label>

                                    {formData.has_ev && (
                                        <>

                                            <label>Commute Days:</label>
                                            <div className="centerItems">
                                                <div className="checkboxGroup">
                                                    {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"].map((day) => (
                                                        <label key={day}>
                                                            <input
                                                                type="checkbox"
                                                                checked={formData.commute_days.includes(day)}
                                                                onChange={() => handleCheckboxChange(day)}
                                                            />
                                                            {day}
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>

                                            <label>
                                                One-way Commute Distance (km):
                                                <input
                                                    type="number"
                                                    className="input"
                                                    value={formData.commute_distance_km}
                                                    onChange={(e) =>
                                                        handleChange("commute_distance_km", parseFloat(e.target.value))
                                                    }
                                                />
                                            </label>


                                            <label>
                                                Number of short trips per week (groceries, appointments, social,...):
                                                <input
                                                    type="number"
                                                    className="input"
                                                    value={formData.non_commute_trips_per_week}
                                                    onChange={(e) =>
                                                        handleChange("non_commute_trips_per_week", parseInt(e.target.value))
                                                    }
                                                />
                                            </label>

                                            <label>
                                                Average one-way short trip distance (km):
                                                <input
                                                    type="number"
                                                    className="input"
                                                    value={formData.non_commute_distance_km}
                                                    onChange={(e) =>
                                                        handleChange("non_commute_distance_km", parseFloat(e.target.value))
                                                    }
                                                />
                                            </label>
                                            <label>
                                                EV Battery Capacity (kWh):
                                                <input
                                                    type="number"
                                                    className="input"
                                                    value={formData.ev_battery_capacity_kwh}
                                                    onChange={(e) =>
                                                        handleChange("ev_battery_capacity_kwh", parseFloat(e.target.value))
                                                    }
                                                />
                                            </label>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                        <p style={{ color: "rgb(240,0,0)", fontSize: "13pt" }}> {errorMessage}</p>
                        <div style={{ textAlign: "center" }}>
                            {isSubmitting ?
                                (<div className="loading-container">
                                    <div className="spinner"></div>
                                </div>
                                ) : (
                                    <div style={{ marginTop: "20px" }}>
                                        <button className="submitButton" onClick={handleSubmit}>
                                            🚀 Explore
                                        </button>
                                    </div>)

                            }

                        </div>
                        {/* <pre style={{ fontSize: "12px", marginTop: "20px", backgroundColor: "rgb(0,0,0)" }}>
                            {JSON.stringify(formData, null, 2)}
                        </pre> */}

                    </div>








                    <div className={`outputSideBar ${results ? 'withBackground' : ''} ${isFading ? "fade-out" : "fade-in"}`}>
                        {results ? (
                            <div className="resultsPanel">
                                <h2>Recommended System</h2>
                                <div className="resultsList">
                                    {Object.entries(results)
                                        .filter(([, [, , , , policy]]) => policy === displayedPolicy)
                                        .map(([ranking, [battery, pv, self_sufficiency, cost, policy, policy_desc, diagram, eue_index, predictions]]) => (
                                            <div
                                                key={ranking}
                                                className={`sectionContentOutput ${fadeOut ? "" : ""}`}
                                            >
                                                <div className="batteryPvValues">
                                                    <div className="valueBlock">
                                                        <div className={`valueTitle ${dragging ? "dragging" : ""}`}>
                                                            <strong>Panels</strong></div>
                                                        <div className="valueNumber">{predictions[70 - selectedIndex][1].toFixed(1)} kW</div>
                                                    </div>
                                                    <div className="valueBlock">
                                                        <div className={`valueTitle ${dragging ? "dragging" : ""}`}>
                                                            <strong>Battery</strong></div>
                                                        <div className="valueNumber">{predictions[70 - selectedIndex][0].toFixed(1)} kWh</div>
                                                    </div>
                                                    <div className="valueBlock">
                                                        <div className={`valueTitle ${dragging ? "dragging" : ""}`}>
                                                            <strong>Estimated Cost</strong></div>
                                                        <div className="valueNumber">£{diagram[selectedIndex].toFixed(0)}</div>
                                                    </div>
                                                </div>
                                            </div>

                                        ))}
                                    {
                                        uniquePolicies.length > 1 ? (
                                            <div>

                                                <h3 className="centerItems">
                                                    Electric Vehicle Charging
                                                </h3>
                                                <div className="policyButtonsRow">
                                                    {uniquePolicies.map((policy) => (
                                                        <button
                                                            key={policy}
                                                            className={`policyButton ${selectedPolicy === policy ? "active" : ""}`}
                                                            onClick={() => {
                                                                setSelectedPolicy(policy);
                                                                handleChangeResults("charging_policy", policy)
                                                            }}
                                                        >
                                                            {policy.replace("_", " ")}
                                                        </button>

                                                    ))}
                                                </div>
                                            </div>

                                        ) : ("")
                                    }
                                    {Object.entries(results)
                                        .filter(([, [, , , , policy]]) => policy === displayedPolicy)
                                        .map(([ranking, [battery, pv, self_sufficiency, cost, policy, policy_desc, diagram, eue_index, predictions]]) => (
                                            <div
                                                key={ranking}
                                                className={`sectionContenOutput ${fadeOut ? "" : ""}`}
                                            >
                                                <div className="centerItems">
                                                    {policy_desc}
                                                </div>

                                                <h3 className="centerItems">
                                                    Cost and Benefits
                                                </h3>

                                                <div className="policyButtonsRow" style={{}}>
                                                    <button
                                                        className={`policyButton ${selectedGraph === "cost" ? "activeLight" : ""}`}
                                                        onClick={() => setSelectedGraph("cost")}
                                                    >
                                                        Cost
                                                    </button>
                                                    <button
                                                        className={`policyButton ${selectedGraph === "savings" ? "activeLight" : ""}`}
                                                        onClick={() => setSelectedGraph("savings")}
                                                    >
                                                        Yearly Savings
                                                    </button>
                                                    <button
                                                        className={`policyButton ${selectedGraph === "payback" ? "activeLight" : ""}`}
                                                        onClick={() => setSelectedGraph("payback")}
                                                    >
                                                        Payback-Time
                                                    </button>
                                                </div>

                                                <div style={{ width: "100%", margin: "auto" }}>
                                                    {selectedGraph === "cost" && (
                                                        <>
                                                            {/* <h2>Cost vs Self-Sufficiency</h2> */}

                                                            <p>
                                                                Estimated cost of a photovoltaic system based on the typical cost of the panels and battery.
                                                            </p>
                                                            <Diagram
                                                                data={diagram.map((y, i) => ({
                                                                    x: 20 + i,
                                                                    y: computeCost(predictions[70 - i][0], predictions[70 - i][1]),
                                                                    y_min: computeCostMin(predictions[70 - i][0], predictions[70 - i][1], i),
                                                                    y_max: computeCostMax(i),
                                                                }))}
                                                                selected_index={selectedIndex}
                                                                onSelectIndex={setSelectedIndex}
                                                                dragging={dragging}
                                                                setDragging={setDragging}
                                                            />
                                                        </>
                                                    )}
                                                    {selectedGraph === "savings" && (
                                                        <>
                                                            {/* <h2>Yearly Savings vs Self-Sufficiency</h2> */}
                                                            <p>
                                                                Yearly savings is based on the avoided grid imports and excess electricity sold back to the grid.
                                                            </p>
                                                            <div key="Grid Tariffs" className="groupRowLight">
                                                                <span><b>Grid Tariffs</b> </span>
                                                                <div key="Buy" className="groupItem">
                                                                    <span>Buy:</span>
                                                                    <input
                                                                        type="number"
                                                                        className="input"
                                                                        style={{ width: "50px" }}
                                                                        value={priceBuy}
                                                                        onChange={(e) =>
                                                                            setPriceBuy(parseFloat(e.target.value))
                                                                        }
                                                                    />
                                                                    £
                                                                </div>
                                                                <div key="Sell" className="groupItem">
                                                                    <span>Sell:</span>
                                                                    <input
                                                                        type="number"
                                                                        className="input"
                                                                        style={{ width: "50px" }}
                                                                        value={priceSell}
                                                                        onChange={(e) =>
                                                                            setPriceSell(parseFloat(e.target.value))
                                                                        }
                                                                    />
                                                                    £
                                                                </div>
                                                            </div>
                                                            <Diagram
                                                                data={diagram.map((y, i) => ({
                                                                    x: 20 + i,
                                                                    y: computeSavings(pvProduction, predictions, totalLoad, priceBuy, priceSell, i),
                                                                    y_min: computeSavingsMin(pvProduction, predictions, totalLoad, priceBuy, priceSell, i),
                                                                    y_max: computeSavingsMax(pvProduction, totalLoad, priceBuy, priceSell, i),
                                                                }))}
                                                                selected_index={selectedIndex}
                                                                onSelectIndex={setSelectedIndex}
                                                                dragging={dragging}
                                                                setDragging={setDragging}
                                                            />
                                                        </>
                                                    )}

                                                    {selectedGraph === "payback" && (
                                                        <>
                                                            <p>
                                                                Number of years required for the investment in a photovoltaic system to pay for itself.
                                                                This is calculated based on the yearly savings.
                                                            </p>
                                                            <div key="Grid Tariffs" className="groupRowLight">
                                                                <span><b>Grid Tariffs</b> </span>
                                                                <div key="Buy" className="groupItem">
                                                                    <span>Buy:</span>
                                                                    <input
                                                                        type="number"
                                                                        className="input"
                                                                        style={{ width: "50px" }}
                                                                        value={priceBuy}
                                                                        onChange={(e) =>
                                                                            setPriceBuy(parseFloat(e.target.value))
                                                                        }
                                                                    />
                                                                    £
                                                                </div>
                                                                <div key="Sell" className="groupItem">
                                                                    <span>Sell:</span>
                                                                    <input
                                                                        type="number"
                                                                        className="input"
                                                                        style={{ width: "50px" }}
                                                                        value={priceSell}
                                                                        onChange={(e) =>
                                                                            setPriceSell(parseFloat(e.target.value))
                                                                        }
                                                                    />
                                                                    £
                                                                </div>
                                                            </div>
                                                            <DiagramPayback
                                                                data={diagram.map((y, i) => ({
                                                                    x: 20 + i,
                                                                    y: y / (Math.max(0, (((pvProduction * predictions[70 - i][1] - ((20 + i) / 100 * totalLoad)) * priceSell))) + (((20 + i) / 100 * totalLoad * priceBuy))),
                                                                }))}
                                                                selected_index={selectedIndex}
                                                                onSelectIndex={setSelectedIndex}
                                                                dragging={dragging}
                                                                setDragging={setDragging}
                                                            />
                                                        </>
                                                    )}
                                                </div>

                                            </div>
                                        ))}
                                </div>

                                <div className="centerItems" style={{ marginTop: "15px" }}>
                                    We estimate that the household consumes <em>{householdLoad} kWh</em> and an EV consumes <em>{evLoad} kWh</em> per year.
                                </div>
                                <div style={{ marginTop: "20px", textAlign: "center" }}>
                                    <button className="validateButton" onClick={openValidateWindow}>
                                        Validate Selection
                                    </button>

                                </div>
                            </div>
                        ) : (
                            <img
                                src="/assets/solar_house.png"
                                alt="Solar Panel House"
                                className={`image ${isFading ? "fade-out" : "fade-in"}`}
                            />
                        )}
                    </div>
                    {showValidateWindow && (
                        <div className="modal-overlay"
                            onClick={() => {
                                if (!isSubmittingValidation) {
                                    closeValidateWindow();
                                }
                            }}>
                            <div className="modal-content" onClick={(e) => e.stopPropagation()}>

                                {isSubmittingValidation ? (
                                    <div className="loading-container">
                                        <div className="spinner"></div>
                                        <p>Validation may take a couple of minutes, please wait...</p>
                                    </div>
                                ) : (
                                    <>
                                        {pv !== -1 && battery !== -1 ? (
                                            // ✅ Show results overlay
                                            <>
                                                {sizingError !== "" ?

                                                    (
                                                        <div className="results">
                                                            <button className="close-button" onClick={closeValidateWindow}>
                                                                &times;
                                                            </button>
                                                            <p><strong>{sizingError}</strong></p>

                                                        </div>
                                                    )
                                                    :
                                                    (
                                                        <div className="results">
                                                            <button className="close-button" onClick={closeValidateWindow}>
                                                                &times;
                                                            </button>
                                                            <p><strong>PV Size:</strong> {pv} kW</p>
                                                            <p><strong>Battery Size:</strong> {battery} kWh</p>
                                                            <p>Self-sufficiency: {selectedIndex + 20}%</p>
                                                            <p>Estimated Cost: £{cost.toFixed(0)}</p>
                                                            <p>Expected Yearly Savings: £{(Math.max(0, (((pvProduction * pv - (formData.self_sufficiency_target / 100 * totalLoad)) * priceSell))) + ((formData.self_sufficiency_target / 100 * totalLoad * priceBuy))).toFixed(0)}</p>
                                                            <p>Payback-time: {(cost / (Math.max(0, (((pvProduction * pv - (formData.self_sufficiency_target / 100 * totalLoad)) * priceSell))) + ((formData.self_sufficiency_target / 100 * totalLoad * priceBuy)))).toFixed(1)} years</p>

                                                        </div>
                                                    )



                                                }
                                            </>
                                        ) : (
                                            <>
                                                <button className="close-button" onClick={closeValidateWindow}>
                                                    &times;
                                                </button>

                                                <h2>Validate Selection</h2>

                                                <p>
                                                    The explorer shows AI-generated values which may deviate from the exact sizing.<br />
                                                    We therefore run a detailed simulation to ensure your selection is 100% accurate.
                                                </p>

                                                <div className="modal-field">
                                                    <label htmlFor="selfSufficiency">
                                                        Self-sufficiency target: {formData.self_sufficiency_target}%
                                                    </label>
                                                    <input
                                                        style={{ width: "95%" }}
                                                        type="range"
                                                        id="selfSufficiency"
                                                        min="20"
                                                        max="90"
                                                        value={formData.self_sufficiency_target}
                                                        onChange={(e) => {
                                                            setSelectedIndex(Number(e.target.value) - 20);
                                                        }}
                                                    />
                                                </div>


                                                {formData.has_ev ? (
                                                    <div className="modal-field">

                                                        <label htmlFor="chargingPolicy">Charging Policy:</label>
                                                        <select
                                                            id="chargingPolicy"
                                                            value={selectedPolicy}
                                                            onChange={
                                                                (e) => {
                                                                    setSelectedPolicy(e.target.value);
                                                                    handleChangeResults("charging_policy", e.target.value)
                                                                }
                                                            }
                                                            className="input"
                                                        >
                                                            <option value="Departure 80%">Departure 80%</option>
                                                            <option value="Arrival 80%">Arrival 80%</option>
                                                            <option value="Arrival Min">Arrival Min</option>
                                                            <option value="Bidirectional">Bidirectional</option>
                                                        </select>
                                                    </div>

                                                ) : ("")}
                                                <div className="centerItems">
                                                    <button className="validate-confirm" onClick={handleSubmitValidate}>
                                                        Run Validation
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}

                            </div>
                        </div>
                    )
                    }




                </div >
            ) : (
                <div className="portrait-warning">
                    Please rotate your device to landscape mode.
                </div>
            )}
        </>
    );
}
